/**
 * PURE client-side honesty mapping for execution outcomes (Story #127). The
 * server sends RAW `outcome` + `reasonCode`; this derives the user-facing label,
 * the defect-stance qualifier, and a badge tone. INVARIANT (unit-tested): the
 * words "defect"/"potential product defect" appear ONLY for a `failed` +
 * `assert_failed` verdict; every other failure carries an explicit "not a product
 * defect" qualifier so the tool never overclaims a defect.
 */

export type OutcomeTone = 'success' | 'danger' | 'warning' | 'neutral';

export interface OutcomeDescription {
  headline: string;
  qualifier: string;
  tone: OutcomeTone;
}

/** The single defect signal: a run that FAILED a storefront assertion. */
function isPotentialDefect(outcome: string, reasonCode: string | null): boolean {
  return outcome === 'failed' && reasonCode === 'assert_failed';
}

export function describeOutcome(
  outcome: string | null,
  reasonCode: string | null,
): OutcomeDescription {
  switch (outcome) {
    case 'validated':
      return {
        headline: 'Validated',
        qualifier: 'This run passed all checks.',
        tone: 'success',
      };
    case 'partially_validated':
      return {
        headline: 'Partially validated',
        qualifier:
          'Some steps validated; others could not be completed. Not a product defect on its own.',
        tone: 'warning',
      };
    case 'failed':
      return isPotentialDefect(outcome, reasonCode)
        ? {
            headline: 'Failed — potential product defect',
            qualifier:
              'A check failed against the storefront. This is a potential product defect for a human to confirm.',
            tone: 'danger',
          }
        : {
            headline: 'Failed',
            qualifier:
              'This run failed, but not on a product assertion — review the step evidence before treating it as a product defect.',
            tone: 'danger',
          };
    case 'flaky':
      return {
        headline: 'Flaky',
        qualifier: 'Inconsistent results across attempts. Not a confirmed product defect.',
        tone: 'warning',
      };
    case 'blocked':
      return {
        headline: 'Blocked',
        qualifier: 'Stopped by a bot or authentication challenge — not a product defect.',
        tone: 'neutral',
      };
    case 'error':
      return {
        headline: 'Error',
        qualifier: 'A tool or environment problem interrupted the run — not a product defect.',
        tone: 'neutral',
      };
    case 'cancelled':
      return {
        headline: 'Cancelled',
        qualifier: 'This run was cancelled before completing. Not a product defect.',
        tone: 'neutral',
      };
    case 'configuration_required':
      return {
        headline: 'Configuration required',
        qualifier:
          'Stopped at a safety boundary that needs configuration to validate — not a product defect.',
        tone: 'warning',
      };
    default:
      return {
        headline: 'Unknown outcome',
        qualifier: 'This run has no recorded outcome — not a product defect.',
        tone: 'neutral',
      };
  }
}
